import { ESubscriptionTier } from "../constants/enums"

/**
 * The PostHog group type for a VidMax team. Matches the `vm_Team` entity.
 *
 * Renaming a group type after data has landed is effectively impossible in PostHog, so
 * this string is load-bearing — do not "tidy" it to workspace/org.
 */
export const VM_TEAM_GROUP_TYPE = "team"

/**
 * Master switch for PostHog group analytics. **Currently off.**
 *
 * VidMax is predominantly a consumer product where a team is one person, so emitting a
 * group per team would mint a group entity for essentially every user. That is billed
 * per group, and it makes group-level analysis useless — a "teams" breakdown where
 * almost every team has one member tells you nothing you could not get per-person.
 *
 * Groups only start paying for themselves once multi-seat plans have real population.
 * Flip this to `true` then; `VM_GROUP_ELIGIBLE_TIERS` already scopes it to those plans,
 * so turning it on does not retroactively enrol the consumer base.
 *
 * Note the asymmetry with `team_id`: that is a plain event property, costs nothing extra,
 * and is emitted unconditionally. Only the group association is gated.
 */
export const VM_GROUP_TRACKING_ENABLED = false

/**
 * Plans that get a PostHog group when tracking is enabled.
 *
 * Multi-seat tiers only. A single-seat team is not a meaningful analytics entity — the
 * person already is one.
 */
export const VM_GROUP_ELIGIBLE_TIERS: readonly ESubscriptionTier[] = [
  ESubscriptionTier.BusinessPlan,
  ESubscriptionTier.AgencyPlan,
]

/**
 * Whether this team should be associated with a PostHog group on an outgoing event.
 *
 * Both conditions must hold: the master switch is on, AND the team is on a multi-seat
 * plan. Callers that cannot resolve a tier pass `undefined` and get `false` — failing
 * closed matters here, because an accidentally-created group cannot be cheaply undone.
 */
export const shouldTrackTeamGroup = (tier: ESubscriptionTier | undefined): boolean =>
  VM_GROUP_TRACKING_ENABLED && tier !== undefined && VM_GROUP_ELIGIBLE_TIERS.includes(tier)
